import React, { useEffect, useState } from 'react';
import { onAuthStateChanged, signInWithCustomToken, signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db, functions } from '../../firebaseConfig';
import { ORGANIZATION_ACCESS_KEY, isClientView } from '../common/organizationAccessMode';
import { organizationAccessRoute } from '../common/organizationAccessRoute';

import { managementOrigin } from '../common/siteEnvironment';
import { OrganizationReportContext } from '../common/reportNavigation';
import { serializeHashRoute } from '../routing/hashRouter';
let bootstrap: Promise<void> | undefined;

function startAccess(): Promise<void> {
    return bootstrap ||= (async () => {
        await auth.authStateReady();
        if (window.location.pathname === '/admin-access' || window.location.pathname === '/client-access') {
            const opener = window.opener;
            const nonce = window.location.hash.slice(1);
            if (!opener || !nonce)
                throw new Error('Open this dashboard from the management organization list.');
            await new Promise<void>((resolve, reject) => {
                let receiving = false;
                const cleanup = () => {
                    clearTimeout(timeout);
                    clearInterval(ready);
                    window.removeEventListener('message', receive);
                };
                const receive = async (event: MessageEvent) => {
                    if (event.origin !== managementOrigin || event.source !== opener || event.data?.nonce !== nonce || receiving)
                        return;
                    if (event.data?.type === 'organization-access-error') {
                        cleanup();
                        reject(new Error('Access was denied. Open the dashboard again from management.'));
                    }
                    if (event.data?.type !== 'organization-access-ticket')
                        return;
                    receiving = true;
                    cleanup();
                    try {
                        const result = await httpsCallable<{ ticket: string }, { token: string }>(functions, 'redeemOrganizationAccess')({ ticket: event.data.ticket });
                        await signInWithCustomToken(auth, result.data.token);
                        const claims = (await auth.currentUser!.getIdTokenResult()).claims;
                        if ((claims.organizationAccessMode === 'client') !== isClientView)
                            throw new Error('Dashboard view does not match this access session.');
                        sessionStorage.setItem(ORGANIZATION_ACCESS_KEY, isClientView ? 'client' : 'active');
                        const requestId = typeof event.data.serviceRequestId === 'string' && /^[a-zA-Z0-9_-]{1,128}$/.test(event.data.serviceRequestId)
                            ? event.data.serviceRequestId : '';
                        const route = organizationAccessRoute(event.data.workflowId, isClientView ? '' : requestId);
                        window.history.replaceState({}, '', `/${route}`);
                        opener.postMessage({ type: 'organization-access-complete', nonce }, managementOrigin);
                        window.opener = null;
                        resolve();
                    } catch {
                        opener.postMessage({ type: 'organization-access-error', nonce }, managementOrigin);
                        reject(new Error('Access failed. Open the dashboard again from management.'));
                    }
                };
                const ready = window.setInterval(() => opener.postMessage({ type: 'organization-access-ready', nonce }, managementOrigin), 250);
                const timeout = window.setTimeout(() => {
                    cleanup();
                    reject(new Error('Access expired. Open the dashboard again from management.'));
                }, 90_000);
                window.addEventListener('message', receive);
            });
        }
        const claims = auth.currentUser && (await auth.currentUser.getIdTokenResult()).claims;
        if (!claims?.organizationAccessSession || (claims.organizationAccessMode === 'client') !== isClientView)
            throw new Error('Access ended. Open the dashboard again from management.');
        await httpsCallable(functions, 'getOrganizationAccess')({});
    })();
}

export default function OrganizationAccessGate({ children }: { children: React.ReactNode }) {
    const [ready, setReady] = useState(false);
    const [error, setError] = useState('');
    const [name, setName] = useState('');
    const [report, setReport] = useState<{ reportId: string; limited: boolean } | null>(null);
    useEffect(() => {
        const entering = window.location.pathname === '/admin-access';
        let disposed = false;
        let unsubscribe = () => {};
        let unsubscribeAuth = () => {};
        const end = (message: string) => {
            setReady(false);
            setError(message);
        };
        startAccess().then(async () => {
            if (disposed)
                return;
            const claims = (await auth.currentUser!.getIdTokenResult()).claims;
            if (disposed)
                return;
            unsubscribeAuth = onAuthStateChanged(auth, user => {
                if (!user)
                    end('Organization access ended. You can close this tab.');
            });
            unsubscribe = onSnapshot(doc(db, claims.reportWorkspace === true ? 'consultant_report_access_sessions' : 'organization_access_sessions', String(claims.organizationAccessSession)), snapshot => {
                const session = snapshot.data();
                if (!session || session.revoked || (session.accessMode === 'client') !== isClientView) {
                    end('Organization access ended. Open it again from management.');
                    return;
                }
                setName(session.organizationName);
                const reportId = session.reportDraftId || session.initialReportDraftId;
                if (reportId && !isClientView) {
                    setReport({ reportId, limited: Boolean(session.reportDraftId) });
                    if (entering || session.reportDraftId)
                        window.history.replaceState({}, '', `/${serializeHashRoute('consultantReport', { reportId })}`);
                }
                setReady(true);
            }, () => end('Organization access is no longer allowed.'));
        }).catch(reason => { if (!disposed) end(reason.message); });
        return () => { disposed = true; unsubscribe(); unsubscribeAuth(); };
    }, []);
    const exit = async () => {
        setReady(false);
        setError('Ending organization access…');
        try {
            await httpsCallable(functions, 'endOrganizationAccess')({});
            await signOut(auth);
            sessionStorage.clear();
            setError('Organization access ended. You can close this tab.');
        } catch {
            setError('Could not end access. Check your connection and retry Exit.');
        }
    };
    return <>
        <div role="status" className={`sticky top-0 z-[100] flex flex-wrap items-center justify-between gap-3 border-b border-amber-300 bg-amber-50 px-5 py-3 text-sm text-amber-950 ${report ? '[color-scheme:light]' : 'dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100'}`}>
            <span><strong>{isClientView ? 'Client view' : 'Admin access'}{name ? `: ${name}` : ''}</strong> · {auth.currentUser?.email} · {isClientView ? 'Read-only. Changes are disabled.' : 'Changes are saved under your account.'}</span>
            <button type="button" onClick={exit} className="rounded border border-current px-3 py-1 font-semibold">Exit</button>
        </div>
        {ready ? <OrganizationReportContext.Provider value={report}>{children}</OrganizationReportContext.Provider>
            : <main className="min-h-screen bg-slate-50 p-10 text-slate-900"><p role={error ? 'alert' : 'status'}>{error || 'Opening organization dashboard…'}</p></main>}
    </>;
}
